import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import ManageUnitsFleetModal from '@/components/modals/ManageUnitsFleetModal';
import AddNewUnitModal from '@/components/modals/unit-registration/AddNewUnitModal';
import Table from '@/components/Table';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useMediaQuery from '@/hooks/useMediaQuery';
import { allUnitsQueryOptions, getLatestOperatingHoursQueryOptions } from '@/service/vpc/queryOptions';
import useUnitsTable from '@/tables/useUnitsTable';

const unitsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  register: z.coerce.boolean().optional(),
  sn: z.string().optional(),
  guid: z.string().optional(),
  origin: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/_account/units/')({
  component: FleetUnitsPage,
  errorComponent: ErrorBoundary,
  validateSearch: unitsSearchSchema,
  loader: async (opts) => {
    const fleetAccountId = opts.context.fleetAccountId;
    if (!fleetAccountId) {
      return; // Return early if no fleet account is selected yet
    }
    const units = await opts.context.queryClient.ensureQueryData(allUnitsQueryOptions());
    return { units };
  },
});

function FleetUnitsPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    page: rawPage,
    register,
    sn,
    origin,
    guid,
  } = useSearch({
    from: '/_authenticated/_account/units/',
  });
  const { mediaQueryNodes: mq } = useMediaQuery();

  const { fleetAccountUnits, isLoading, userHasPermission, userHasCapability } = useFleetAccount();
  const { data: allUnits } = useQuery(allUnitsQueryOptions());

  const fleetUnitIds = new Set(fleetAccountUnits.map((u) => u.endCustomerUnitId));
  const claimingUnits = allUnits?.filter((u) => u.claiming && !fleetUnitIds.has(u.endCustomerUnitId)) ?? [];
  const units = [...fleetAccountUnits, ...claimingUnits];
  // BDIP rejects the whole batch with 403 if any unit lacks telematics.view — e.g. a unit that is
  // still being claimed (AB#1640822, AB#1644382).
  const endCustomerUnitIds = units
    .map((unit) => unit.endCustomerUnitId)
    .filter((id) => userHasCapability('unit.telematics.view', id));

  const hasSwDownload = userHasPermission('swdownload.read');

  const { data: latestOperatingHours, isLoading: isLoadingHours } = useQuery(
    getLatestOperatingHoursQueryOptions({
      endcustomerunitids: endCustomerUnitIds,
    })
  );

  const { columns, selectedRows } = useUnitsTable({
    latestOperatingHours,
    isLoadingHours,
    showInstallation: hasSwDownload,
  });

  const mqSize = mq({
    default: 'mobile',
    md: 'desktop',
  });

  // Clear query params when modal is closed after auto-open
  const handleModalClose = () => {
    if (register) {
      navigate({
        to: '.',
        search: { page: page > 1 ? page : undefined },
        replace: true,
      });
    }
  };

  const page = rawPage ?? 1;

  return (
    <>
      <Table
        key={mqSize}
        isLoading={isLoading}
        data={units}
        columns={columns}
        columnVisibility={{
          chassisIdentifiers: false,
        }}
        defaultSorting={[
          {
            id: 'unitName',
            desc: false,
          },
        ]}
        headerTitle={t(($) => $.pages.account.unit_overview.title)}
        pageIndex={page}
        onPageChange={(pageIndex) => {
          navigate({
            to: '.',
            search: { page: pageIndex > 1 ? pageIndex : undefined },
            replace: true,
          });
        }}
        onRowClick={(row) => {
          navigate({
            to: '/units/$endCustomerUnitId/specifications',
            params: {
              endCustomerUnitId: row.endCustomerUnitId.toString(),
            },
            state: {
              page: page > 1 ? page : undefined,
            },
          });
        }}
        onMouseOver={(row) =>
          router.preloadRoute({
            to: '/units/$endCustomerUnitId/specifications',
            params: {
              endCustomerUnitId: row.endCustomerUnitId.toString(),
            },
            state: { page: page > 1 ? page : undefined },
          })
        }
        actions={
          <AddNewUnitModal
            initialSerialNumber={sn}
            autoOpen={register === true && !!sn}
            autoSearch={register === true && !!sn}
            origin={origin}
            qrCode={guid}
            onClose={handleModalClose}
          />
        }
        selectedActions={() => (
          <div className="flex gap-4">
            <ManageUnitsFleetModal
              operation="new"
              preSelectedRows={selectedRows}
              buttonText={t(($) => $.pages.account.unit_overview.add_to_fleet)}
            />
          </div>
        )}
      />
    </>
  );
}
